import { styled } from "goober";
import { useEffect, useRef, type ReactNode } from "react";
import { navEnterStyles } from "../hooks/useNavTransition";
import { isEditableTarget } from "../lib/isEditableTarget";
import { Icon } from "./Icon";
import { isInAppNavigation } from "./Link";
import { MenuItemLink } from "./MenuItem";
import {
  executionsShortcut,
  type PanelShortcut,
  type PanelSide,
  PanelCollapseButton,
  sidePanelWidth,
} from "./PinnedPanel";
import { usePanelTransition } from "./PanelTransition";
import { Text } from "./Text";

const Container = styled("aside")`
  display: flex;
  flex-direction: column;
  width: ${sidePanelWidth}px;
  flex-shrink: 0;
  min-height: 0;
  background: var(--surface-secondary);
  border-left: 0.5px solid var(--border-alpha);

  &[data-side="left"] {
    border-left: none;
    border-right: 0.5px solid var(--border-alpha);
  }
`;

const Header = styled("div")`
  ${navEnterStyles}
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  height: 40px;
  padding: 4px 8px;
  flex-shrink: 0;
  border-bottom: 0.5px solid var(--border-alpha);
  position: relative;
  z-index: 1;
`;

const Actions = styled("div")`
  display: flex;
  align-items: center;
  gap: 2px;
`;

// The 4px gap after the 24px collapse button lands its icon and the title on
// the rows' icon and label columns below.
const Title = styled("div")`
  display: flex;
  align-items: center;
  gap: 4px;
  min-width: 0;
`;

const List = styled("div")`
  ${navEnterStyles}
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding: 4px;
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  overflow-x: hidden;
  overscroll-behavior: contain;
`;

export const sidebarRowStyles = `
  flex-shrink: 0;
  min-width: 0;
  &[aria-current="true"] {
    background: var(--alpha4);
  }
  &:focus-visible {
    outline: none;
  }
`;

const Row = styled(MenuItemLink)`
  ${sidebarRowStyles}
`;

// A row with nowhere to go, laid out like a link row.
const StaticRow = styled("div")`
  ${sidebarRowStyles}
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px;
  border-radius: 8px;
  &:hover {
    background: var(--alpha4);
  }
`;

const RowIcon = styled("span")`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  flex-shrink: 0;
`;

const RowText = styled("div")`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
`;

export function ExecutionSidebarRow({
  to,
  selected,
  label,
  icon,
  onNavigate,
  children,
}: {
  to?: string;
  selected: boolean;
  label: string;
  icon: ReactNode;
  onNavigate?: () => void;
  children: ReactNode;
}) {
  const content = (
    <>
      <RowIcon>{icon}</RowIcon>
      <RowText>
        <Text variant="text-small-md" ellipsis>
          {label}
        </Text>
        <Text variant="text-small" ellipsis color="var(--text-secondary)">
          {children}
        </Text>
      </RowText>
      {selected && (
        <Icon name="check" size={12} color="var(--text-secondary)" />
      )}
    </>
  );
  if (to === undefined) {
    return (
      <StaticRow aria-current={selected ? "true" : undefined} title={label}>
        {content}
      </StaticRow>
    );
  }
  return (
    <Row
      to={to}
      onClick={(event) => {
        if (isInAppNavigation(event) && event.detail > 0)
          event.currentTarget.focus({ preventScroll: true });
        onNavigate?.();
      }}
      aria-current={selected ? "true" : undefined}
      title={label}
    >
      {content}
    </Row>
  );
}

export function Sidebar({
  title,
  side = "right",
  selectedId,
  actions,
  collapse,
  arrowNavigation = false,
  onNavigate,
  children,
}: {
  title: string;
  side?: PanelSide;
  selectedId?: string;
  actions?: ReactNode;
  collapse?: { shortcut: PanelShortcut; onCollapse: () => void };
  arrowNavigation?: boolean;
  onNavigate?: () => void;
  children: ReactNode;
}) {
  const direction = usePanelTransition();
  const listRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!arrowNavigation) return;
    const onKeyDown = (event: KeyboardEvent) => {
      const list = listRef.current;
      if (
        event.defaultPrevented ||
        event.isComposing ||
        event.ctrlKey ||
        event.metaKey ||
        event.shiftKey ||
        (event.key !== "ArrowDown" && event.key !== "ArrowUp") ||
        isEditableTarget(event) ||
        (event.target instanceof Element && event.target.closest("select")) ||
        document.querySelector(
          'dialog[open], [role="dialog"], [data-popover]'
        ) ||
        !list ||
        list.closest('[inert], [hidden], [aria-hidden="true"]')
      )
        return;
      const focused =
        event.target instanceof Element && list.contains(event.target)
          ? event.target.closest("a")
          : null;
      if (!event.altKey && !focused) return;
      event.preventDefault();
      event.stopPropagation();
      if (event.repeat) return;
      const selected =
        (!event.altKey && focused) ||
        list.querySelector('a[aria-current="true"]');
      const next = selected
        ? event.key === "ArrowDown"
          ? selected.nextElementSibling
          : selected.previousElementSibling
        : list.querySelector("a");
      if (next instanceof HTMLAnchorElement) {
        if (focused) next.focus({ preventScroll: true });
        onNavigate?.();
        history.replaceState(history.state, "", next.href);
      }
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [arrowNavigation, onNavigate]);
  useEffect(() => {
    const list = listRef.current;
    const selected = list?.querySelector('[aria-current="true"]');
    if (!list || !selected) return;
    const bounds = list.getBoundingClientRect();
    const row = selected.getBoundingClientRect();
    if (row.top < bounds.top) list.scrollTop += row.top - bounds.top;
    else if (row.bottom > bounds.bottom)
      list.scrollTop += row.bottom - bounds.bottom;
  }, [selectedId]);
  return (
    <Container aria-label={title} data-side={side}>
      <Header data-nav-enter={direction}>
        <Title>
          {collapse && (
            <PanelCollapseButton
              label={`Unpin ${title.toLowerCase()} panel`}
              side={side}
              shortcut={collapse.shortcut}
              onPress={collapse.onCollapse}
            />
          )}
          <Text variant="text-small-md" color="var(--text-primary)">
            {title}
          </Text>
        </Title>
        <Actions>{actions}</Actions>
      </Header>
      <List ref={listRef} data-nav-enter={direction}>
        {children}
      </List>
    </Container>
  );
}

export function ExecutionSidebar({
  selectedId,
  loading,
  empty,
  actions,
  onCollapse,
  onNavigate,
  children,
}: {
  selectedId?: string;
  loading: boolean;
  empty: boolean;
  actions?: ReactNode;
  onCollapse: () => void;
  onNavigate?: () => void;
  children: ReactNode;
}) {
  return (
    <Sidebar
      title="Executions"
      arrowNavigation
      onNavigate={onNavigate}
      selectedId={selectedId}
      actions={actions}
      collapse={{ shortcut: executionsShortcut, onCollapse }}
    >
      {children}
      {empty && (
        <Text
          variant="text-small"
          color="var(--text-secondary)"
          style={{ padding: 8 }}
        >
          {loading ? "Loading executions…" : "No executions yet"}
        </Text>
      )}
    </Sidebar>
  );
}
